<template>
  <div class="pt-8 flex flex-col lg:flex-row p-4 min-h-screen bg-light-bg dark:bg-dark-bg dark:text-dark-text">
    <AppSidebar />

    <div class="flex flex-col flex-grow p-6 lg:p-12 space-y-4 h-full">
      <div v-if="message" :class="messageType" 
          class="fixed top-0 left-0 right-0 z-5 p-4 rounded-lg mb-4 text-center 
                  bg-white dark:bg-dark-box-bg dark:text-dark-text dark:border 
                  dark:border-dark-border">
          {{ message }}
          <div class="absolute bottom-0 left-0 h-1 rounded-lg" 
              :style="{ width: progress + '%', backgroundColor: getProgressColor(), borderRadius: '8px' }">
          </div>
      </div>
      <h1 class="text-xl md:text-2xl font-bold mb-4 text-center lg:text-left">Kierowcy</h1>

      <div class="bg-white p-4 rounded-lg shadow-md w-full h-auto overflow-visible dark:bg-dark-box-bg dark:border dark:border-dark-border">
        <div class="flex flex-col md:flex-row items-center justify-between mb-4 space-y-4 md:space-y-0">
          <h2 class="text-lg font-semibold text-gray-700 dark:text-dark-text">Lista Kierowców</h2>

          <button @click="goToArchive()" class="px-4 py-2 text-sm font-medium bg-teal-700 text-white rounded-md hover:bg-teal-800 dark:bg-teal-600 dark:hover:bg-teal-500 transition-colors"> Archiwum </button>

          <div class="flex items-center space-x-4">
            <label for="status-filter" class="text-sm font-medium text-gray-700 dark:text-gray-300">Filtruj według statusu:</label>
            <select id="status-filter" v-model="selectedStatus" @change="filterByStatus"
                    class="block pl-3 pr-10 py-2 text-sm bg-light-bg text-light-text border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md dark:bg-dark-box-bg dark:text-dark-text dark:border-dark-border w-full sm:w-48">
              <option value="">Wszystkie statusy</option>
              <option value="DOSTEPNY" class="text-dark-status-available dark:text-dark-status-available">Dostępny</option>
              <option value="NIEDOSTEPNY" class="text-orange-500 dark:text-orange-500">Niedostępny</option>
              <option value="W_TRAKCIE" class="text-dark-status-pending dark:text-dark-status-pending">W trakcie</option>
              <option value="PRZYJETE" class="text-blue-500 dark:text-blue-500">Przyjęte</option>
              <!-- <option value="ZAKONCZONY" class="text-dark-status-unavailable dark:text-dark-status-unavailable">Zakończony</option> -->
            </select>
          </div>
        </div>

        <div class="w-full mb-4">
          <DynamicSearch
              :data="drivers"
              :keysToSearch="['firstName', 'lastName', 'city', 'phone', 'email', 'car', 'driverStatus']"
              @search-results="handleSearchResults"
              @clear-search="clearSearch"
              class="mt-4 dark:bg-dark-box-bg text-gray-900 dark:text-dark-text w-full md:w-auto"
          />
        </div>

        <div class="overflow-x-auto w-full">
          <table class="min-w-full bg-white border border-gray-300 rounded-lg dark:bg-dark-table-bg dark:border-dark-border">
            <thead>
            <tr class="bg-gray-50 dark:bg-dark-table-header dark:text-dark-text">
              <th class="py-3 px-4 border-b border-gray-300 dark:border-dark-border text-left text-sm font-semibold text-gray-700 dark:text-dark-text">Nazwisko</th>
              <th class="py-3 px-4 border-b border-gray-300 dark:border-dark-border text-left text-sm font-semibold text-gray-700 dark:text-dark-text">Imię</th>
              <th class="py-3 px-4 border-b border-gray-300 dark:border-dark-border text-left text-sm font-semibold text-gray-700 dark:text-dark-text">Miasto</th>
              <th class="py-3 px-4 border-b border-gray-300 dark:border-dark-border text-left text-sm font-semibold text-gray-700 dark:text-dark-text">Telefon</th>
              <th class="py-3 px-4 border-b border-gray-300 dark:border-dark-border text-left text-sm font-semibold text-gray-700 dark:text-dark-text">Samochód</th>
              <th class="py-3 px-4 border-b border-gray-300 dark:border-dark-border text-left text-sm font-semibold text-gray-700 dark:text-dark-text">Rejestracja</th>
              <th class="py-3 px-4 border-b border-gray-300 dark:border-dark-border text-left text-sm font-semibold text-gray-700 dark:text-dark-text">Rok</th>
              <th class="py-3 px-4 border-b border-gray-300 dark:border-dark-border text-left text-sm font-semibold text-gray-700 dark:text-dark-text">Adres Zamieszkania</th>
              <th class="py-3 px-4 border-b border-gray-300 dark:border-dark-border text-left text-sm font-semibold text-gray-700 dark:text-dark-text">Status</th>
              <!-- <th class="py-3 px-4 border-b border-gray-300 dark:border-dark-border text-right text-sm font-semibold text-gray-700 dark:text-dark-text">Akcje</th> -->
            </tr>
            </thead>
            <tbody>
            <tr v-if="filteredDrivers.length === 0">
              <td colspan="9" class="text-center py-4 text-red-500 font-semibold dark:text-dark-status-unavailable">Brak kierowców</td>
            </tr>
            <tr v-else 
                v-for="(driver) in paginatedDrivers" 
                :key="driver.id" 
                :class="[
                    'border-b border-gray-300 dark:border-dark-border hover:bg-light-hover dark:hover:bg-dark-hover hover:cursor-pointer',
                    selectedDriver && driver.id === selectedDriver.id ? 'bg-light-hover dark:bg-dark-hover' : ''
                ]"
                @click="showDriverDetails(driver)"
            >
              <td class="py-3 px-4 dark:text-dark-text">{{ driver.lastName }}</td>
              <td class="py-3 px-4 dark:text-dark-text">{{ driver.firstName }}</td>
              <td class="py-3 px-4 dark:text-dark-text">{{ driver.city }}</td>
              <td class="py-3 px-4 dark:text-dark-text">{{ driver.phone }}</td>
              <td class="py-3 px-4 dark:text-dark-text">{{ driver.car }}</td>
              <td class="py-3 px-4 dark:text-dark-text">{{ driver.registrationNumber || "Brak rejestracji" }}</td>
              <td class="py-3 px-4 dark:text-dark-text">{{ driver.carYear }}</td>
              <td class="py-3 px-4 dark:text-dark-text">{{ driver.homeAddress }}</td>
              <td class="py-3 px-4 dark:text-dark-text">
                <!-- Dodajemy kolorowe badge'y dla statusu -->
                <span v-if="driver.driverStatus === 'DOSTEPNY'"
                      class="inline-flex items-center bg-green-500 hover:bg-green-600 text-white text-xs font-medium px-2 py-1 rounded-full focus:outline-none transition-all truncate">
                  <span class="w-1.5 h-1.5 bg-white rounded-full mr-1.5"></span>
                  DOSTĘPNY
                </span>
                <span v-else-if="driver.driverStatus === 'NIEDOSTEPNY'"
                      class="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium px-2 py-1 rounded-full focus:outline-none transition-all truncate">
                  <span class="w-1.5 h-1.5 bg-white rounded-full mr-1.5"></span>
                  NIEDOSTĘPNY
                </span>
                <span v-else-if="driver.driverStatus === 'W_TRAKCIE'"
                      class="inline-flex items-center bg-yellow-500 hover:bg-yellow-600 text-white text-xs font-medium px-2 py-1 rounded-full focus:outline-none transition-all truncate">
                  <span class="w-1.5 h-1.5 bg-white rounded-full mr-1.5"></span>
                  W TRAKCIE
                </span>
                <span v-else-if="driver.driverStatus === 'AWARIA'"
                      class="inline-flex items-center bg-red-600 hover:bg-red-700 text-white text-xs font-medium px-2 py-1 rounded-full focus:outline-none transition-all truncate">
                  <span class="w-1.5 h-1.5 bg-white rounded-full mr-1.5"></span>
                  AWARIA
                </span>
                <span v-else-if="driver.driverStatus === 'PRZYJETE'"
                      class="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-2 py-1 rounded-full focus:outline-none transition-all truncate">
                  <span class="w-1.5 h-1.5 bg-white rounded-full mr-1.5"></span>
                  PRZYJETE
                </span>
                <!-- <span v-else-if="driver.driverStatus === 'ZAKONCZONY'"
                      class="inline-flex items-center bg-red-500 hover:bg-red-600 text-white text-xs font-medium px-2 py-1 rounded-full focus:outline-none transition-all truncate">
                  <span class="w-1.5 h-1.5 bg-white rounded-full mr-1.5"></span>
                  ZAKOŃCZONY
                </span> -->
              </td>
              <!-- <td class="py-3 px-4 text-right">
                <div class="relative inline-block text-left">
                  <button @click.stop="toggleDropdown(driver.id)" class="text-gray-500 hover:text-gray-700 dark:text-dark-text">
                    <EllipsisVerticalIcon class="h-5 w-5" />
                  </button>
                  <div v-if="dropdownOpen === driver.id" class="origin-top-right absolute left-0 ml-[-10rem] mt-[-2.2rem] w-40 focus:outline-none">
                    <div class="py-1.5 absolute flex space-x-2 bg-gray-200 dark:bg-gray-900 rounded">
                      <button @click.stop="goToProfile(driver)" class="text-left px-2 py-2 text-sm text-teal-700 dark:text-teal-400 flex items-center space-x-2">
                        <UserIcon class="w-4 h-4" />
                      </button>
                      <button @click.stop="openEditModal(driver)" class="text-left px-2 py-2 text-sm text-teal-700 dark:text-teal-400 flex items-center space-x-2">
                        <PencilIcon class="w-4 h-4" />
                      </button>
                      <button v-if="['ROLE_ADMIN'].includes(user.role)" @click.stop="deleteDriver(driver.id)" class="text-left px-2 py-2 text-sm text-teal-700 dark:text-teal-400 flex items-center space-x-2">
                        <TrashIcon class="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </td> -->
            </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginacja -->
        <div class="mt-4 flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
          <div class="text-center sm:text-left text-sm text-light-text dark:text-gray-400">
            Wyświetlono {{ (currentPage - 1) * itemsPerPage + paginatedDrivers.length }} z {{ filteredDrivers.length }} kierowców
          </div>

          <div class="flex flex-wrap justify-center sm:justify-end items-center gap-2">
            <button v-if="currentPage > 1"
                    @click="goToPage(1)"
                    class="px-3 py-1 text-sm font-medium bg-light-table-header rounded-md dark:bg-dark-table-header dark:text-dark-text">
              Pierwsza strona
            </button>

            <!-- Przycisk "Poprzednia" -->
            <button v-if="currentPage > 1"
                    @click="prevPage"
                    class="px-3 py-1 text-sm font-medium bg-light-table-header rounded-md dark:bg-dark-table-header dark:text-dark-text">
              &laquo; Poprzednia
            </button>

            <!-- Widoczne strony -->
            <button v-for="page in visiblePages"
                    :key="page"
                    @click="goToPage(page)"
                    :class="[currentPage === page ? 'bg-teal-700 text-white' : 'bg-gray-200 dark:bg-dark-box-bg text-gray-700 dark:text-dark-text']"
                    class="px-3 py-1 rounded-lg text-sm cursor-pointer">
              {{ page }}
            </button>

            <!-- Następna strona -->
            <button v-if="currentPage < totalPages"
                    @click="nextPage"
                    class="px-3 py-1 text-sm font-medium bg-light-table-header rounded-md dark:bg-dark-table-header dark:text-dark-text">
              Następna &raquo;
            </button>

            <!-- Ostatnia strona -->
            <button v-if="currentPage < totalPages"
                    @click="goToPage(totalPages)"
                    class="px-3 py-1 text-sm font-medium bg-light-table-header rounded-md dark:bg-dark-table-header dark:text-dark-text">
              Ostatnia strona
            </button>
          </div>
        </div>
      </div>
    </div>

    <DriverCreateModal
        :isOpen="isCreateModalOpen"
        :errors="errors"
        @close="closeCreateModal"
        @submit="handleCreateSubmit"
    >
    </DriverCreateModal>

    <DriverEditModal
        :isOpen="isEditModalOpen"
        :editingDriver="editingDriver"
        :errors="errors"
        @close="closeEditModal"
        @submit="handleEditSubmit"
    >
      <template>
        <div class="flex justify-between mt-4">
          <button type="button" @click="cancelEdit" class="text-gray-500 hover:text-gray-700 dark:text-dark-text">
            Anuluj
          </button>
          <button type="submit" class="w-full bg-gray-800 text-white py-2 rounded-md hover:bg-teal-700 focus:outline-none focus:ring focus:ring-indigo-500 dark:bg-dark-status-finished dark:hover:bg-dark-status-pending">
            Zapisz zmiany
          </button>
        </div>
      </template>
    </DriverEditModal>

    <DriverInformationModal
            v-if="showModal"
            :visible="showModal"
            :title="'Szczegóły Kierowcy #' + (selectedDriver?.id || '')"
            :selectedDriver="selectedDriver"
            :userRole="user.role"
            @close="closeModalInformation"
            @goToProfile="goToProfile"
            @openEditModal="openEditModal"
            @deleteDriver="deleteDriver"
            @updateLocked="locked => selectedDriver.locked = locked"
      >
          <div class="space-y-4">
            <p><b>ID Kierowcy:</b> {{ selectedDriver?.id }}</p>
            <p><b>Imie:</b> {{ selectedDriver?.firstName }}</p>
            <p><b>Nazwisko:</b> {{ selectedDriver?.lastName }}</p>
            <p><b>Telefon:</b> {{ selectedDriver?.phone }}</p>
            <p><b>Samochód:</b> {{ selectedDriver?.car }}</p>
            <p><b>Rok samochodu:</b> {{ selectedDriver?.carYear }}</p>
            <p><b>Numer rejestracji:</b> {{ selectedDriver?.registrationNumber }}</p>
            <p><b>Miasto:</b> {{ selectedDriver?.city }}</p>
            <p><b>Adres domu:</b> {{ selectedDriver?.homeAddress }}</p>
            <p><b>Nazwa użytkownika: </b> {{ selectedDriver?.username }}</p>
          </div>
    </DriverInformationModal>

    <div v-if="['ROLE_ADMIN', 'ROLE_ADULT_DISPATCHER'].includes(user.role)" class="mb-4">
      <button
          :disabled="hasErrors"
          v-if="!isCreateModalOpen"
          @click="openCreateModal"
          class="fixed bottom-8 right-8 bg-button-bg text-button-text p-4 rounded-full shadow-md transition-transform duration-200 ease-in-out hover:bg-button-hover focus:outline-none focus:ring dark:bg-dark-button-bg dark:text-dark-text dark:hover:bg-dark-button-hover"
      >
        <PlusIcon class="h-6 w-6 transition-transform duration-200 ease-in-out transform hover:rotate-45"/>
      </button>
    </div>
  </div>
</template>

<script>
import {useAuthStore} from '@/stores/authStore';
import driversService from '@/services/driversService';
import AppSidebar from '@/components/AppSidebar.vue';
import DriverCreateModal from './DriverCreateModal.vue';
import DriverEditModal from './DriverEditModal.vue';
import DynamicSearch from '../search/DynamicSearch.vue';
import {PlusIcon} from "@heroicons/vue/24/solid";
import DriverInformationModal from './DriverInformationModal.vue';
import { useSessionStore } from '@/stores/useSessionStore';

export default {
  name: "DriversPageDispatcher",
  components: {
    PlusIcon,
    AppSidebar,
    DriverCreateModal,
    DriverEditModal,
    DynamicSearch,
    DriverInformationModal
  },
  setup() {
    const authStore = useAuthStore();
    const sessionStore = useSessionStore();
    if (authStore.isAuthenticated) {
        authStore.checkExpiration();
    }
    return {user: authStore.user, sessionStore};
  },
  data() {
    return {
      message: '',
      messageType: '',
      progress: 0,
      drivers: [],
      filteredDrivers: [],
      editingDriver: null,
      selectedStatus: '',
      newDriver: {
        firstName: '',
        lastName: '',
        city: '',
        phone: '',
        car: '',
        carYear: '',
        homeAddress: '',
        driverStatus: 'DOSTEPNY'
      },
      errors: {},
      isCreateModalOpen: false,
      isEditModalOpen: false,
      dropdownOpen: null,
      loading: false,
      showModal: false,
      selectedDriver: null,

      // Dodaj zmienne paginacji:
      currentPage: 1,
      itemsPerPage: 5, // Liczba elementów na stronę
      totalPages: 0, // Łączna liczba stron
      visiblePages: [], // Widoczne numery stron
      isSearching: false, // Flaga kontrolująca wyszukiwanie
    };
  },
  computed: {
    paginatedDrivers() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredDrivers.slice(start, end);
    },
    hasErrors() {
      return Object.values(this.errors).some(error => error.length > 0);
    }
  },
  mounted() {
    this.calculateItemsPerPage();
    window.addEventListener("resize", this.calculateItemsPerPage);

    const pageFromUrl = parseInt(this.$route.query.currentPage) || 1;
    this.currentPage = pageFromUrl;

    this.fetchDrivers().then(() => {
      this.calculateTotalPages();
    });

    this.refreshDrivers();
  },
  beforeUnmount() {
    if (this.sessionStore.intervalId) {
      clearInterval(this.sessionStore.intervalId); // Oczyść interval, jeśli jest ustawiony
      this.sessionStore.intervalId = null; // Reset interval to prevent any issues if it tries to be cleared again
    }
    window.removeEventListener("resize", this.calculateItemsPerPage);
  },
  methods: {
    refreshDrivers() {
      this.sessionStore.startInterval(() => {
          if (!this.isSearching) {
            this.fetchDrivers();
          }
        }, 10000);
    },
    calculateItemsPerPage() {
      const width = window.innerWidth;

      if (width >= 3840) {
        this.itemsPerPage = 45; // 4K monitor
      } else if (width >= 2560) {
        this.itemsPerPage = 35; // 2K monitor
      } else if (width >= 1920) {
        this.itemsPerPage = 25; // Full HD monitor
      } else {
        this.itemsPerPage = 15; // Standardowa wartość
      }

      this.calculateTotalPages();
    },
    showDriverDetails(driver) {
      this.selectedDriver = driver; // Przypisz wybrane zamówienie
      this.showModal = true; // Pokaż modal
    },
    closeModalInformation() {
      this.selectedDriver = null; // Wyczyść dane zamówienia
      this.showModal = false; // Zamknij modal
    },
    calculateTotalPages() {
      this.totalPages = Math.ceil(this.filteredDrivers.length / this.itemsPerPage) || 1;
      this.updateVisiblePages();
    },
    updateVisiblePages() {
      const range = 5; // Liczba stron widocznych na raz
      const start = Math.max(1, this.currentPage - Math.floor(range / 2));
      const end = Math.min(start + range - 1, this.totalPages);

      this.visiblePages = Array.from({length: end - start + 1}, (_, i) => start + i);
    },
    goToPage(page) {
      this.currentPage = page;
      this.updateVisiblePages();
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.updateVisiblePages();
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.updateVisiblePages();
      }
    },
    showMessage(msg, type) {
      this.message = msg;
      this.messageType = type === 'success' ? 'success-message' : 'error-message';
      this.progress = 0;

      const interval = 30;
      const duration = 5000;
      const step = 100 / (duration / interval);

      const progressInterval = setInterval(() => {
        this.progress += step;
        if (this.progress >= 100) {
          clearInterval(progressInterval);
          this.progress = 0;
          this.message = '';
          this.messageType = '';
        }
      }, interval);
    },
    getProgressColor() {
      return this.messageType === 'success-message' ? '#16a34a' : '#b91c1c';
    },
    toggleDropdown(driverId) {
      this.dropdownOpen = this.dropdownOpen === driverId ? null : driverId;
    },
    openCreateModal() {
      this.isCreateModalOpen = true;
    },
    closeCreateModal() {
      this.isCreateModalOpen = false;
    },
    openEditModal(driver) {
      this.editingDriver = driver;
      this.isEditModalOpen = true;
      this.showModal = false;
    },
    closeEditModal() {
      this.isEditModalOpen = false;
      this.editingDriver = null;
    },
    handleSearchResults(filteredData) {
      this.filteredDrivers = filteredData;
      this.filterByStatus();

      this.calculateTotalPages();
      this.filteredDrivers = filteredData;

      this.isSearching = filteredData.length > 0;

      this.goToPage(1);
    },
    clearSearch() {
      this.isSearching = false;

      this.goToPage(1);
    },
    filterByStatus() {
      if (this.selectedStatus) {
        this.filteredDrivers = this.drivers.filter(driver => driver.driverStatus === this.selectedStatus);
        this.isSearching = true;
      } else {
        this.filteredDrivers = this.drivers;
        this.isSearching = false;
      }
      this.calculateTotalPages();

      this.goToPage(1);
    },
    async handleCreateSubmit(newDriver) {
      if (this.hasErrors) return;
      if (!this.loading) {
        this.loading = true;
        try {
          await driversService.createDriver(newDriver);
          await this.fetchDrivers();
          this.closeCreateModal();
          this.showMessage("Kierowca został pomyślnie dodany.", "success");
        } catch (error) {
          console.error('Błąd podczas tworzenia kierowcy:', error);
          this.showMessage("Wystąpił błąd podczas dodawania kierowcy.", "error");
        } finally {
          this.loading = false;
        }
      }
    },
    async handleEditSubmit(updatedDriver) {
      if (!this.loading) {
        this.loading = true;
        try {
          await driversService.updateDriver(updatedDriver.id, updatedDriver);
          await this.fetchDrivers();
          this.closeEditModal();
          this.showMessage("Kierowca został pomyślnie zaktualizowany.", "success");
        } catch (error) {
          console.error('Błąd podczas aktualizacji kierowcy:', error);
          this.showMessage("Wystąpił błąd podczas aktualizacji kierowcy.", "error");
        } finally {
          this.loading = false;
        }
      }
    },
    async fetchDrivers() {
      try {
        const response = await driversService.getAllDrivers();
        this.drivers = response.data.sort((a, b) => {
          return a.lastName.localeCompare(b.lastName, 'pl', { sensitivity: 'base' });
        });
        this.filteredDrivers = this.drivers;
      } catch (error) {
        console.error('Błąd podczas pobierania kierowców:', error);
      }
    },
    async deleteDriver(driverId) {
      if (confirm("Czy na pewno chcesz usunąć tego kierowcę?")) {
        try {
          await driversService.deleteDriver(driverId);
          await this.fetchDrivers();
          this.showMessage("Kierowca został pomyślnie usunięty.", "success");
          this.closeModalInformation();
        } catch (error) {
          console.error('Błąd podczas usuwania kierowcy:', error);
          if (error.response && error.response.status === 409) {
            this.showMessage("Nie możemy usunąć kierowcy, ponieważ należy do zleceń oraz posiada swoje rozliczenie.", "error");
          } else {
            this.showMessage("Wystąpił błąd podczas usuwania kierowcy.", "error");
          }
          this.closeModalInformation();
        }
      }
    },
    goToProfile(driver) {
      const currentPage = this.currentPage; 
      if (this.user.role === 'ROLE_ADMIN') {
        this.$router.push({
          name: 'DriverProfileAdmin',
          params: { id: driver.id },
          query: { currentPage }
        });
      } else {
        this.$router.push({
          name: 'DriverProfileDispatcher',
          params: { id: driver.id },
          query: { currentPage }
        });
      }
    },
    cancelEdit() {
      this.editingDriver = null;
      this.closeEditModal();
    },
    goToArchive() {
      const currentPage = this.currentPage; 

      if (this.user.role === 'ROLE_ADMIN') {
        this.$router.push({
          name: 'DriversArchivePageAdmin', 
          query: {
            returnTo: this.$route.name,
            currentPage: currentPage,
          }
        });
      } else {
        this.$router.push({
          name: 'DriversArchivePageDispatcher', 
          query: {
            returnTo: this.$route.name,
            currentPage: currentPage,
          }
        });
      }
    },
  }
};
</script>
